import React, { useState, useCallback } from 'react';
import {
  View, Text, StyleSheet, FlatList, TouchableOpacity,
  Modal, Pressable, TextInput, ActivityIndicator,
  ScrollView, Alert,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useFocusEffect } from '@react-navigation/native';
import apiClient from '../utils/axiosConfig';

const PILOT_COLORS = ['#1d4ed8', '#059669', '#7c3aed', '#dc2626', '#d97706', '#0891b2'];

export default function OperatorFleetScreen() {
  const [fleet, setFleet]           = useState([]);
  const [pilots, setPilots]         = useState([]);
  const [loading, setLoading]       = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [addModal, setAddModal]     = useState(false);
  const [assignModal, setAssignModal] = useState(null); // aircraft seleccionado

  // Form de nueva aeronave
  const [form, setForm] = useState({ name: '', brand: '', model: '', serial_number: '', weight: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [fleetRes, missionsRes] = await Promise.all([
        apiClient.get('/operator-fleet/'),
        apiClient.get('/operator-missions/'),
      ]);
      setFleet(fleetRes.data);
      setPilots(missionsRes.data.pilots || []);
    } catch (e) {
      // silencioso
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useFocusEffect(useCallback(() => { load(); }, [load]));

  const pilotColor = (id) => PILOT_COLORS[id % PILOT_COLORS.length];

  const createAircraft = async () => {
    if (!form.name || !form.brand || !form.model || !form.serial_number || !form.weight) {
      Alert.alert('Campos requeridos', 'Completa todos los campos.');
      return;
    }
    setSaving(true);
    try {
      await apiClient.post('/operator-fleet/', { ...form, weight: parseFloat(form.weight) });
      setAddModal(false);
      setForm({ name: '', brand: '', model: '', serial_number: '', weight: '' });
      await load(true);
    } catch (e) {
      Alert.alert('Error', e?.response?.data?.error || 'No se pudo crear la aeronave.');
    } finally {
      setSaving(false);
    }
  };

  const togglePilot = async (aircraft, pilot) => {
    const isAssigned = (aircraft.pilots || []).some(p => p.id === pilot.id);
    try {
      if (isAssigned) {
        await apiClient.delete(`/operator-fleet/${aircraft.id}/assign-pilot/`, { data: { pilot_id: pilot.id } });
      } else {
        await apiClient.post(`/operator-fleet/${aircraft.id}/assign-pilot/`, { pilot_id: pilot.id });
      }
      await load(true);
      // Actualizar el modal con datos frescos
      setAssignModal(prev => {
        if (!prev) return null;
        const updated = fleet.find(a => a.id === aircraft.id);
        return updated || prev;
      });
    } catch (e) {
      Alert.alert('Error', e?.response?.data?.error || 'Error al actualizar asignación.');
    }
  };

  const renderAircraft = ({ item }) => (
    <View style={styles.card}>
      <View style={styles.cardHeader}>
        <View style={styles.cardIcon}>
          <Ionicons name="airplane" size={20} color="#1d4ed8" />
        </View>
        <View style={styles.cardInfo}>
          <Text style={styles.cardName}>{item.name}</Text>
          <Text style={styles.cardSub}>{item.brand} {item.model}</Text>
          <Text style={styles.cardSerial}>S/N: {item.serial_number}</Text>
        </View>
        <TouchableOpacity style={styles.assignBtn} onPress={() => setAssignModal(item)}>
          <Ionicons name="people-outline" size={15} color="#7c3aed" />
          <Text style={styles.assignBtnText}>Pilotos</Text>
        </TouchableOpacity>
      </View>
      {(item.pilots || []).length > 0 && (
        <View style={styles.pilotRow}>
          {item.pilots.map(p => (
            <View key={p.id} style={[styles.pilotChip, { borderColor: pilotColor(p.id) }]}>
              <View style={[styles.pilotDot, { backgroundColor: pilotColor(p.id) }]} />
              <Text style={styles.pilotChipText}>{p.name}</Text>
            </View>
          ))}
        </View>
      )}
      {(item.pilots || []).length === 0 && (
        <Text style={styles.noPilots}>Sin pilotos asignados</Text>
      )}
    </View>
  );

  if (loading) return (
    <View style={styles.center}><ActivityIndicator size="large" color="#1d4ed8" /></View>
  );

  return (
    <View style={styles.container}>
      <FlatList
        data={fleet}
        keyExtractor={i => String(i.id)}
        renderItem={renderAircraft}
        contentContainerStyle={{ padding: 16, paddingBottom: 100 }}
        refreshing={refreshing}
        onRefresh={() => { setRefreshing(true); load(true); }}
        ListEmptyComponent={
          <View style={styles.empty}>
            <Ionicons name="airplane-outline" size={40} color="#d1d5db" />
            <Text style={styles.emptyText}>Sin aeronaves registradas</Text>
            <Text style={styles.emptySub}>Agrega tu primera aeronave</Text>
          </View>
        }
      />

      {/* FAB */}
      <TouchableOpacity style={styles.fab} onPress={() => setAddModal(true)}>
        <Ionicons name="add" size={26} color="#fff" />
      </TouchableOpacity>

      {/* Modal nueva aeronave */}
      <Modal visible={addModal} transparent animationType="slide" onRequestClose={() => setAddModal(false)}>
        <Pressable style={styles.modalOverlay} onPress={() => setAddModal(false)}>
          <Pressable style={styles.modalBox} onPress={e => e.stopPropagation?.()}>
            <Text style={styles.modalTitle}>Nueva aeronave</Text>
            {[
              { key: 'name',          label: 'Nombre / Identificador', placeholder: 'ej. DJI T40 #1' },
              { key: 'brand',         label: 'Marca',                  placeholder: 'ej. DJI' },
              { key: 'model',         label: 'Modelo',                 placeholder: 'ej. Agras T40' },
              { key: 'serial_number', label: 'Número de serie',        placeholder: 'ej. 1ZNBC9F0D12345' },
              { key: 'weight',        label: 'Peso (kg)',              placeholder: 'ej. 24.5', keyboard: 'decimal-pad' },
            ].map(f => (
              <View key={f.key} style={styles.inputGroup}>
                <Text style={styles.inputLabel}>{f.label}</Text>
                <TextInput
                  style={styles.input}
                  placeholder={f.placeholder}
                  placeholderTextColor="#9ca3af"
                  keyboardType={f.keyboard || 'default'}
                  value={form[f.key]}
                  onChangeText={v => setForm(prev => ({ ...prev, [f.key]: v }))}
                />
              </View>
            ))}
            <TouchableOpacity style={styles.saveBtn} onPress={createAircraft} disabled={saving}>
              {saving
                ? <ActivityIndicator size="small" color="#fff" />
                : <Text style={styles.saveBtnText}>Guardar aeronave</Text>}
            </TouchableOpacity>
            <TouchableOpacity style={styles.cancelBtn} onPress={() => setAddModal(false)}>
              <Text style={styles.cancelBtnText}>Cancelar</Text>
            </TouchableOpacity>
          </Pressable>
        </Pressable>
      </Modal>

      {/* Modal asignación de pilotos */}
      <Modal visible={!!assignModal} transparent animationType="fade" onRequestClose={() => setAssignModal(null)}>
        <Pressable style={styles.modalOverlay} onPress={() => setAssignModal(null)}>
          <Pressable style={styles.modalBox} onPress={e => e.stopPropagation?.()}>
            <Text style={styles.modalTitle}>Pilotos — {assignModal?.name}</Text>
            <Text style={styles.modalSub}>{assignModal?.brand} {assignModal?.model}</Text>
            {pilots.length === 0 && (
              <Text style={styles.noPilots}>No hay pilotos en tu empresa</Text>
            )}
            {pilots.map(pilot => {
              const currentAircraft = fleet.find(a => a.id === assignModal?.id);
              const isAssigned = (currentAircraft?.pilots || []).some(p => p.id === pilot.id);
              return (
                <TouchableOpacity
                  key={pilot.id}
                  style={[styles.pilotOption, isAssigned && styles.pilotOptionActive]}
                  onPress={() => togglePilot(currentAircraft, pilot)}
                >
                  <View style={[styles.pilotOptionIcon, { backgroundColor: pilotColor(pilot.id) + '22' }]}>
                    <Ionicons
                      name={isAssigned ? 'checkmark-circle' : 'person-outline'}
                      size={18}
                      color={isAssigned ? '#16a34a' : pilotColor(pilot.id)}
                    />
                  </View>
                  <Text style={[styles.pilotOptionText, isAssigned && { color: '#16a34a', fontWeight: '700' }]}>
                    {pilot.name}
                  </Text>
                  {isAssigned && <Text style={styles.assignedBadge}>Asignado</Text>}
                </TouchableOpacity>
              );
            })}
            <TouchableOpacity style={styles.cancelBtn} onPress={() => setAssignModal(null)}>
              <Text style={styles.cancelBtnText}>Cerrar</Text>
            </TouchableOpacity>
          </Pressable>
        </Pressable>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  container:        { flex: 1, backgroundColor: '#f9fafb' },
  center:           { flex: 1, justifyContent: 'center', alignItems: 'center' },
  card:             { backgroundColor: '#fff', borderRadius: 12, padding: 14, marginBottom: 10, shadowColor: '#000', shadowOpacity: 0.06, shadowRadius: 4, elevation: 2 },
  cardHeader:       { flexDirection: 'row', alignItems: 'flex-start' },
  cardIcon:         { width: 38, height: 38, borderRadius: 10, backgroundColor: '#eff6ff', justifyContent: 'center', alignItems: 'center', marginRight: 12 },
  cardInfo:         { flex: 1 },
  cardName:         { fontSize: 15, fontWeight: '700', color: '#111827' },
  cardSub:          { fontSize: 12, color: '#6b7280', marginTop: 1 },
  cardSerial:       { fontSize: 11, color: '#9ca3af', marginTop: 1 },
  assignBtn:        { flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 10, paddingVertical: 6, borderRadius: 8, backgroundColor: '#f5f3ff', borderWidth: 1, borderColor: '#ede9fe' },
  assignBtnText:    { fontSize: 12, fontWeight: '600', color: '#7c3aed' },
  pilotRow:         { flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginTop: 10 },
  pilotChip:        { flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 8, paddingVertical: 4, borderRadius: 20, borderWidth: 1, backgroundColor: '#f9fafb' },
  pilotDot:         { width: 7, height: 7, borderRadius: 4 },
  pilotChipText:    { fontSize: 11, color: '#374151', fontWeight: '500' },
  noPilots:         { fontSize: 12, color: '#9ca3af', marginTop: 8, fontStyle: 'italic' },
  empty:            { alignItems: 'center', marginTop: 60, gap: 8 },
  emptyText:        { fontSize: 16, fontWeight: '600', color: '#374151' },
  emptySub:         { fontSize: 13, color: '#9ca3af' },
  fab:              { position: 'absolute', bottom: 28, right: 20, width: 52, height: 52, borderRadius: 26, backgroundColor: '#1d4ed8', justifyContent: 'center', alignItems: 'center', shadowColor: '#1d4ed8', shadowOpacity: 0.4, shadowRadius: 8, elevation: 6 },
  // Modal
  modalOverlay:     { flex: 1, backgroundColor: 'rgba(0,0,0,0.45)', justifyContent: 'flex-end' },
  modalBox:         { backgroundColor: '#fff', borderTopLeftRadius: 20, borderTopRightRadius: 20, padding: 24, paddingBottom: 36 },
  modalTitle:       { fontSize: 17, fontWeight: '700', color: '#111827', marginBottom: 4 },
  modalSub:         { fontSize: 13, color: '#6b7280', marginBottom: 16 },
  inputGroup:       { marginBottom: 12 },
  inputLabel:       { fontSize: 12, fontWeight: '600', color: '#374151', marginBottom: 4 },
  input:            { borderWidth: 1, borderColor: '#e5e7eb', borderRadius: 10, paddingHorizontal: 12, paddingVertical: 10, fontSize: 14, color: '#111827', backgroundColor: '#f9fafb' },
  saveBtn:          { backgroundColor: '#1d4ed8', borderRadius: 10, padding: 14, alignItems: 'center', marginTop: 4 },
  saveBtnText:      { color: '#fff', fontWeight: '700', fontSize: 15 },
  cancelBtn:        { padding: 14, alignItems: 'center', marginTop: 4 },
  cancelBtnText:    { color: '#6b7280', fontSize: 14 },
  pilotOption:      { flexDirection: 'row', alignItems: 'center', paddingVertical: 12, paddingHorizontal: 8, borderRadius: 10, marginBottom: 4 },
  pilotOptionActive:{ backgroundColor: '#f0fdf4' },
  pilotOptionIcon:  { width: 32, height: 32, borderRadius: 16, justifyContent: 'center', alignItems: 'center', marginRight: 12 },
  pilotOptionText:  { flex: 1, fontSize: 14, color: '#374151' },
  assignedBadge:    { fontSize: 11, color: '#16a34a', fontWeight: '600' },
});
